import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';
import { ConfigConstants } from '../../../config/config-constants';
import { TIMERECORDING_TYPE_OF_WORK_CODES } from '../../../models/complex/time-recording-entry.model';
import { FormParticipantTitles } from '../../../types/form-participant-titles';
import { Mutable } from '../../../types/mutable';
import { selectIsCustomerDamageEnabled, selectIsFocReplacementEnabled } from '../../config/selectors/config.selector';
import { LoadStatus } from '../../load-status';
import {
    VALUE_HELPER_FEATURE_KEY,
    ValueHelperCollectionState,
    ValueHelperState,
} from '../value-helper-collection.state';

export const selectValueHelperFeature = createFeatureSelector<ValueHelperCollectionState>(VALUE_HELPER_FEATURE_KEY);

export const selectValueHelperModels = createSelector(selectValueHelperFeature, (state) => state.valueHelpers);

/** Raw load status of the slice. */
export const selectValueHelperStatus: MemoizedSelector<object, LoadStatus> = createSelector(
    selectValueHelperFeature,
    (state) => state.status,
);

/** Whether the value-help slice has been populated from the backend. */
export const selectValueHelperLoaded: MemoizedSelector<object, boolean> = createSelector(
    selectValueHelperFeature,
    (state) => state.status === LoadStatus.Loaded,
);

/**
 * Sort value helpers by their numeric `sortField`, mirroring
 * `ValueHelperService.sortBySortFieldNumeric`. Operates on a copy so the frozen slice is
 * never mutated. Non-numeric sort fields are pushed to the end.
 *
 * @param valueHelpers the value helpers to sort (not mutated)
 * @returns a new, sorted array
 */
function sortBySortFieldNumeric(valueHelpers: ValueHelperState[]): ValueHelperState[] {
    return [...valueHelpers].sort((valueHelper1, valueHelper2) => {
        const valueHelper1numeric = parseInt(valueHelper1.sortField, 10);
        const valueHelper2numeric = parseInt(valueHelper2.sortField, 10);
        if (isNaN(valueHelper1numeric)) {
            return 1;
        }
        if (isNaN(valueHelper2numeric)) {
            return -1;
        }
        return valueHelper1numeric - valueHelper2numeric;
    });
}

/**
 * Sort value helpers by their `code` string. Operates on a copy so the frozen slice is never mutated.
 *
 * @param valueHelpers the value helpers to sort (not mutated)
 * @returns a new, sorted array
 */
function sortByCode(valueHelpers: ValueHelperState[]): ValueHelperState[] {
    return [...valueHelpers].sort((a, b) => (a.code < b.code ? -1 : 1));
}

/**
 * Sort value helpers by their `codeLevel`. Operates on a copy so the frozen slice is never mutated.
 *
 * @param valueHelpers the value helpers to sort (not mutated)
 * @returns a new, sorted array
 */
function sortByCodeLevel(valueHelpers: ValueHelperState[]): ValueHelperState[] {
    return [...valueHelpers].sort((a, b) => (a.codeLevel < b.codeLevel ? -1 : 1));
}

/**
 * Sort value helpers by their `sortField` as a string (lexicographic). Operates on a copy so the
 * frozen slice is never mutated.
 *
 * @param valueHelpers the value helpers to sort (not mutated)
 * @returns a new, sorted array
 */
function sortBySortFieldString(valueHelpers: ValueHelperState[]): ValueHelperState[] {
    return [...valueHelpers].sort((a, b) => (a.sortField < b.sortField ? -1 : 1));
}

/**
 * Value helpers for the given ID, filtered and numerically sorted — the store-backed
 * replacement for `ValueHelperService.getValueHelpersById`. Exposed as `ValueHelperState[]`
 * (the raw slice objects; the facade wraps them into model instances).
 *
 * @param id the value-help ID to filter by
 * @returns a memoized selector of the matching, sorted value helpers
 */
export const selectValueHelpersById = (id: string): MemoizedSelector<object, ValueHelperState[]> =>
    createSelector(selectValueHelperModels, (models) =>
        sortBySortFieldNumeric(models.filter((model) => model.id === id)),
    );

/**
 * Quality-control questions, sorted by codeLevel. Mirrors `ValueHelperService.getQualityControlQuestions`.
 */
export const selectQualityControlQuestions: MemoizedSelector<object, ValueHelperState[]> = createSelector(
    selectValueHelpersById(ConfigConstants.valueHelperIDQualityControlQuestions),
    (valueHelpers) => sortByCodeLevel(valueHelpers),
);

/**
 * Dangerous-goods warnings, sorted by code. Mirrors `ValueHelperService.getDangerousGoodWarning`.
 */
export const selectDangerousGoodWarning: MemoizedSelector<object, ValueHelperState[]> = createSelector(
    selectValueHelpersById(ConfigConstants.valueHelperIDDangerousGoodWarning),
    (valueHelpers) => sortByCode(valueHelpers),
);

/**
 * Travel office-start entries, sorted by sortField (string). Mirrors `ValueHelperService.getTravelOfficeStart`.
 */
export const selectTravelOfficeStart: MemoizedSelector<object, ValueHelperState[]> = createSelector(
    selectValueHelpersById(ConfigConstants.valueHelperIDTravelOfficeStart),
    (valueHelpers) => sortBySortFieldString(valueHelpers),
);

/**
 * Billable time-recording codes: the base billable codes, plus customer-damage codes when
 * enabled, and — when FOC replacement is enabled — the courtesy code removed and the FOC
 * additional labor codes appended. Mirrors `ValueHelperService.getTimeRecordingBillableCodes`.
 */
export const selectTimeRecordingBillableCodes: MemoizedSelector<object, ValueHelperState[]> = createSelector(
    selectValueHelpersById(ConfigConstants.valueHelperIDTimeRecordingBillable),
    selectValueHelpersById(ConfigConstants.valueHelperIdCustomerDamageCodes),
    selectValueHelpersById(ConfigConstants.valueHelperIdFocAdditionalLaborCodes),
    selectIsCustomerDamageEnabled,
    selectIsFocReplacementEnabled,
    (billable, customerDamage, focAdditionalLabor, customerDamageEnabled, focReplacementEnabled) => {
        let codes = [...billable];
        if (customerDamageEnabled) {
            codes = codes.concat(customerDamage);
        }
        if (focReplacementEnabled) {
            codes = codes
                .filter((code) => code.code !== ConfigConstants.BILLABLE_CODE.COURTESY)
                .concat(focAdditionalLabor);
        }
        return codes;
    },
);

/**
 * Billable material codes (returned unmapped; the facade wraps them in DisplayDecorators):
 * base billable codes, plus customer-damage codes when enabled, and — when FOC replacement
 * is enabled — the courtesy code removed and the FOC additional material codes appended.
 * Mirrors the code-selection portion of `ValueHelperService.getBillable`.
 */
export const selectBillableCodes: MemoizedSelector<object, ValueHelperState[]> = createSelector(
    selectValueHelpersById(ConfigConstants.valueHelperIDBillable),
    selectValueHelpersById(ConfigConstants.valueHelperIdCustomerDamageCodes),
    selectValueHelpersById(ConfigConstants.valueHelperIdFocAdditionalMaterialCodes),
    selectIsCustomerDamageEnabled,
    selectIsFocReplacementEnabled,
    (billable, customerDamage, focAdditionalMaterial, customerDamageEnabled, focReplacementEnabled) => {
        let codes = [...billable];
        if (customerDamageEnabled) {
            codes = codes.concat(customerDamage);
        }
        if (focReplacementEnabled) {
            codes = codes
                .filter((code) => code.code !== ConfigConstants.BILLABLE_CODE.COURTESY)
                .concat(focAdditionalMaterial);
        }
        return codes;
    },
);

/**
 * Deterministic replacement for the lazily-populated `ValueHelperService.codeListForId`.
 * When FOC replacement is enabled, exposes the FOC additional labor/material code lists
 * (keyed by their value-help ID) so the confirmation and material-consumption screens can
 * test membership without depending on the order in which the billable getters were called.
 */
export const selectCodeListForId: MemoizedSelector<object, Record<string, string[]>> = createSelector(
    selectValueHelpersById(ConfigConstants.valueHelperIdFocAdditionalLaborCodes),
    selectValueHelpersById(ConfigConstants.valueHelperIdFocAdditionalMaterialCodes),
    selectIsFocReplacementEnabled,
    (focAdditionalLabor, focAdditionalMaterial, focReplacementEnabled) => {
        const codeListForId: Record<string, string[]> = {};
        if (focReplacementEnabled) {
            codeListForId[ConfigConstants.valueHelperIdFocAdditionalLaborCodes] = focAdditionalLabor.map(
                (valueHelper) => valueHelper.code,
            );
            codeListForId[ConfigConstants.valueHelperIdFocAdditionalMaterialCodes] = focAdditionalMaterial.map(
                (valueHelper) => valueHelper.code,
            );
        }
        return codeListForId;
    },
);

/**
 * Reasons for missing protocols, filtered by `codeLevel`.
 * Mirrors `ValueHelperService.getReasonsMissingProtocolsByCode`.
 *
 * @param codeLevel the code level to filter by
 */
export const selectReasonsMissingProtocolsByCode = (codeLevel: string): MemoizedSelector<object, ValueHelperState[]> =>
    createSelector(selectValueHelpersById(ConfigConstants.valueHelperIDMissingProtocols), (valueHelpers) =>
        valueHelpers.filter((valueHelper) => valueHelper.codeLevel === codeLevel),
    );

/**
 * Equipment error codes (P58), keeping entries with an empty `codeLevel` or a matching one.
 * Mirrors `ValueHelperService.getEquipmentErrorCode`.
 *
 * @param codeLevel the code level to filter by
 */
export const selectEquipmentErrorCode = (codeLevel: string): MemoizedSelector<object, ValueHelperState[]> =>
    createSelector(selectValueHelpersById(ConfigConstants.valueHelperIDEquipmentErrorCode), (valueHelpers) =>
        valueHelpers.filter((vh) => vh.codeLevel === '' || vh.codeLevel === codeLevel),
    );

/**
 * Time-recording delivery values, filtered by delivery type (`codeLevel`).
 * Mirrors `ValueHelperService.getTimeRecordingDeliveryValues`.
 *
 * @param deliveryType the delivery type to filter by
 */
export const selectTimeRecordingDeliveryValues = (deliveryType: string): MemoizedSelector<object, ValueHelperState[]> =>
    createSelector(selectValueHelpersById(ConfigConstants.valueHelperIDTimeRecordingDelivery), (valueHelpers) =>
        valueHelpers.filter((vh) => vh.codeLevel === deliveryType),
    );

/**
 * Time-recording booking-category values, filtered by delivery type (`codeLevel`).
 * Mirrors `ValueHelperService.getTimeRecordingBookingCategoryValues`.
 *
 * @param deliveryType the delivery type to filter by
 */
export const selectTimeRecordingBookingCategoryValues = (
    deliveryType: string,
): MemoizedSelector<object, ValueHelperState[]> =>
    createSelector(selectValueHelpersById(ConfigConstants.valueHelperIDTimeRecordingBookingCategory), (valueHelpers) =>
        valueHelpers.filter((vh) => vh.codeLevel === deliveryType),
    );

/**
 * PDA-config value helpers. Mirrors `ValueHelperService.getPdaConfigValues`.
 */
export const selectPdaConfigValues: MemoizedSelector<object, ValueHelperState[]> = selectValueHelpersById(
    ConfigConstants.valueHelperIDPdaConfig,
);

/**
 * Label for the given notification type, or an empty string when none matches.
 * Mirrors `ValueHelperService.getNotificationTypeLabel`.
 *
 * @param notificationType the notification type code to look up
 */
export const selectNotificationTypeLabel = (
    notificationType: ConfigConstants.NotificationTypes,
): MemoizedSelector<object, string> =>
    createSelector(selectValueHelpersById(ConfigConstants.valueHelperIDNotificationType), (valueHelpers) => {
        const type = valueHelpers.find((vh) => vh.code === notificationType);
        return type ? type.descText : '';
    });

/**
 * The value helper matching the given modality code, or `undefined`.
 * Mirrors `ValueHelperService.getModalityValue`.
 *
 * @param modality the modality code to look up
 */
export const selectModalityValue = (modality: string): MemoizedSelector<object, ValueHelperState | undefined> =>
    createSelector(selectValueHelpersById(ConfigConstants.valueHelperIDModalityValues), (valueHelpers) =>
        valueHelpers.find((vh) => vh.code === modality),
    );

/**
 * The OSR-limit PDA-config value helper, or `undefined`. Mirrors `ValueHelperService.getOsrLimitValueHelper`.
 */
export const selectOsrLimitValueHelper: MemoizedSelector<object, ValueHelperState | undefined> = createSelector(
    selectPdaConfigValues,
    (pdaConfigValues) => pdaConfigValues.find((pdaConfig) => pdaConfig.code === ConfigConstants.configKeyOsrLimitAA),
);

/**
 * Whether the phone-fix indicator PDA-config value is present and has a description.
 * Mirrors `ValueHelperService.getPhoneFixIndicator`.
 */
export const selectPhoneFixIndicator: MemoizedSelector<object, boolean> = createSelector(
    selectPdaConfigValues,
    (pdaConfigValues) => {
        const phoneFixIndicator = pdaConfigValues.find(
            (pdaConfig) => pdaConfig.code === ConfigConstants.configKeyPhoneFixIndicatorAA,
        );
        return !!phoneFixIndicator && !!phoneFixIndicator.descText;
    },
);

/**
 * The time-recording activity-type code for the given delivery type, falling back to the
 * default activity type. Mirrors `ValueHelperService.getTimeRecordingActivityType`.
 *
 * @param deliveryType the delivery type (`codeLevel`) to look up
 */
export const selectTimeRecordingActivityType = (deliveryType: string): MemoizedSelector<object, string> =>
    createSelector(selectValueHelpersById(ConfigConstants.valueHelperIDTimeRecordingActivityType), (valueHelpers) => {
        const activityType = valueHelpers.find((vh) => vh.codeLevel === deliveryType);
        return activityType ? activityType.code : ConfigConstants.timeRecordingActivityType;
    });

/**
 * The customer-signature agreement text clause, or an empty string when none is present.
 * Mirrors `ValueHelperService.getCustomerSignatureAgreementTextClause` — including its
 * `typeof value !== undefined` predicate, which always holds and so picks the first entry.
 */
export const selectCustomerSignatureAgreementTextClause: MemoizedSelector<object, string> = createSelector(
    selectValueHelpersById(ConfigConstants.valueHelperIDTextClause),
    (valueHelpers) => {
        const textClause = valueHelpers.find((value) => typeof value !== undefined);
        return textClause ? textClause.descText : '';
    },
);

/**
 * Remove the phone-fix related task-type codes from the given value helpers. Moved verbatim
 * from `ValueHelperService.removePhoneFixTaskTypeCode`.
 *
 * @param valueHelper the task-type value helpers
 * @returns the value helpers without the phone-fix codes
 */
function removePhoneFixTaskTypeCode(valueHelper: ValueHelperState[]): ValueHelperState[] {
    const phoneFixTypeCode: TIMERECORDING_TYPE_OF_WORK_CODES[] = [];

    phoneFixTypeCode.push(TIMERECORDING_TYPE_OF_WORK_CODES.PHONE_FIX);
    phoneFixTypeCode.push(TIMERECORDING_TYPE_OF_WORK_CODES.PHONE_FIX_EXPENSES);
    phoneFixTypeCode.push(TIMERECORDING_TYPE_OF_WORK_CODES.ROSR);
    phoneFixTypeCode.push(TIMERECORDING_TYPE_OF_WORK_CODES.FCC);
    phoneFixTypeCode.push(TIMERECORDING_TYPE_OF_WORK_CODES.ROSN);
    phoneFixTypeCode.push(TIMERECORDING_TYPE_OF_WORK_CODES.FCCN);

    return valueHelper.filter((values) => !phoneFixTypeCode.includes(<TIMERECORDING_TYPE_OF_WORK_CODES>values.code));
}

/**
 * Keep only the remote-work task-type codes appropriate for the current status. Moved from
 * `ValueHelperService.filterRemoteWorkTaskTypeCode`, with the `StatusModel` replaced by the
 * two booleans it previously derived so no domain model leaks into the selector.
 *
 * @param valueHelper the task-type value helpers
 * @param isRemoteWorkOrTraining whether the status is remote work or training
 * @param isRemoteSupport whether the status is remote support
 * @returns the value helpers limited to the applicable remote-work codes
 */
function filterRemoteWorkTaskTypeCode(
    valueHelper: ValueHelperState[],
    isRemoteWorkOrTraining: boolean,
    isRemoteSupport: boolean,
): ValueHelperState[] {
    const remoteWorkTypeCode: TIMERECORDING_TYPE_OF_WORK_CODES[] = [];

    remoteWorkTypeCode.push(TIMERECORDING_TYPE_OF_WORK_CODES.BREAK);
    remoteWorkTypeCode.push(TIMERECORDING_TYPE_OF_WORK_CODES.WAIT);

    if (isRemoteWorkOrTraining) {
        remoteWorkTypeCode.push(TIMERECORDING_TYPE_OF_WORK_CODES.ROSR);
        remoteWorkTypeCode.push(TIMERECORDING_TYPE_OF_WORK_CODES.ROSN);
    } else if (isRemoteSupport) {
        remoteWorkTypeCode.push(TIMERECORDING_TYPE_OF_WORK_CODES.FCC);
        remoteWorkTypeCode.push(TIMERECORDING_TYPE_OF_WORK_CODES.FCCN);
    }

    return valueHelper.filter((values) => remoteWorkTypeCode.includes(<TIMERECORDING_TYPE_OF_WORK_CODES>values.code));
}

/**
 * Task-type info for remote work: when working remotely, only the applicable remote-work codes
 * are kept; otherwise the phone-fix codes are removed. Mirrors
 * `ValueHelperService.getRemoteWorkTimeRecordingTaskTypeInfo`; the status is passed as the
 * three primitive flags it previously read.
 *
 * @param isRemoteWork whether the status is remote work
 * @param isRemoteWorkOrTraining whether the status is remote work or training
 * @param isRemoteSupport whether the status is remote support
 */
export const selectRemoteWorkTimeRecordingTaskTypeInfo = (
    isRemoteWork: boolean,
    isRemoteWorkOrTraining: boolean,
    isRemoteSupport: boolean,
): MemoizedSelector<object, ValueHelperState[]> =>
    createSelector(selectValueHelpersById(ConfigConstants.valueHelperIDTimeRecordingTaskTypeInfo), (valueHelpers) =>
        isRemoteWork
            ? filterRemoteWorkTaskTypeCode(valueHelpers, isRemoteWorkOrTraining, isRemoteSupport)
            : removePhoneFixTaskTypeCode(valueHelpers),
    );

/**
 * Attendee titles as a `FormParticipantTitles` map, keyed by description text.
 * Mirrors `ValueHelperService.getParticipantTitles`.
 */
export const selectParticipantTitles: MemoizedSelector<object, FormParticipantTitles> = createSelector(
    selectValueHelpersById(ConfigConstants.valueHelperIDAttendeeTitles),
    (attendeeTitles) => {
        const participantTitles: Mutable<FormParticipantTitles> = {};

        attendeeTitles.forEach((attendeTitle) => {
            participantTitles[attendeTitle.descText] = {
                displayValue: attendeTitle.descText,
                value: attendeTitle.descText,
            };
        });

        return participantTitles;
    },
);
